import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { forwardRef, useState } from 'react';
import styled from '@emotion/styled';
// Lucide React 직접 import - Vite 번들링 최적화 및 초기화 순서 문제 해결
import { Edit } from 'lucide-react';
import { Trash2 } from 'lucide-react';
import { Button } from '@edusense/shared-ui-kit';
// import { TransformedCourseDraft } from '@edusense/hooks/useCourseDraftData'; // 데이터 타입 임포트
import { formatDistanceToNow } from 'date-fns';
import { ko } from 'date-fns/locale'; // 한국어 로케일
import { useDeleteCourseDraft } from '@edusense/course-client';
import toast from 'react-hot-toast'; // react-hot-toast 임포트
import { useQueryClient } from '@tanstack/react-query'; // useQueryClient 임포트 추가
// 카드 전체 컨테이너
const CardContainer = styled.div`
  background-color: var(--my-card-background);
  border-radius: 0.5rem; /* lg */
  border: 1px solid #e5e7eb; // 테두리 색상 직접 지정 (gray-200)
  overflow: hidden;
  transition: box-shadow 0.2s ease-in-out;
  display: flex;
  flex-direction: column;
  height: 100%; /* 부모 요소에 맞춰 높이 채우기 */
  box-shadow: var(--shadow-sm); // 기본 그림자 추가

  &:hover {
    box-shadow: var(--shadow-md); // 호버 시 더 강한 그림자
  }
`;
// 카드 내용 컨테이너
const ContentContainer = styled.div`
  padding: 1rem; /* p-4 */
  display: flex;
  flex-direction: column;
  flex-grow: 1; /* 남은 공간 채우기 */
`;
// 카드 헤더 (제목과 더보기 버튼)
const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem; /* mb-2 */
  flex-wrap: wrap;
  gap: 0.5rem;
`;
// 제목과 뱃지 래퍼
const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  min-width: 0; // flexbox에서 overflow 처리를 위해 필요
`;
// 카드 제목
const Title = styled.h3`
  font-size: 1rem; /* text-base */
  font-weight: 600; /* font-semibold */
  color: var(--theme-text);
  line-height: 1.4;
  // 두 줄 이상 시 말줄임 처리
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: break-word; // 단어 단위 줄바꿈 개선
  flex: 1;
  min-width: 0; // flexbox에서 overflow 처리를 위해 필요
`;
// 발행 상태 뱃지
const PublishBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;

  ${(props)=>props.isPublished ? `
    background-color: var(--theme-success-bg, #d1fae5);
    color: var(--theme-success-text, #065f46);
    html.dark &, body.vscode-dark & {
      background-color: var(--theme-success-bg-dark, #064e3b);
      color: var(--theme-success-text-dark, #a7f3d0);
    }
  ` : `
    background-color: var(--theme-warning-bg, #fef3c7);
    color: var(--theme-warning-text, #92400e);
    html.dark &, body.vscode-dark & {
      background-color: var(--theme-warning-bg-dark, #78350f);
      color: var(--theme-warning-text-dark, #fde68a);
    }
  `}
`;
// 마지막 수정 시간
const UpdatedAt = styled.p`
  font-size: 0.75rem; /* text-xs */
  color: var(--theme-text-secondary);
  margin-top: auto; /* 내용을 아래로 밀어냄 */
  padding-top: 0.75rem; /* pt-3, 제목과 구분 */
`;
// 액션 버튼 컨테이너
const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem; /* gap-2 */
  border-top: 1px solid var(--my-card-border);
  padding: 0.75rem 1rem; /* px-4 py-3 */
  background-color: var(--my-card-background-subtle); /* 약간 다른 배경색 */
`;
// React.forwardRef 사용하여 ref를 받을 수 있도록 수정
const CourseDraftCard = /*#__PURE__*/ forwardRef(({ draft, onEdit, onDelete }, ref // 두 번째 인자로 ref를 받음
)=>{
    const queryClient = useQueryClient();
    const [isDeleting, setIsDeleting] = useState(false);
    // 자동 생성된 mutations 사용
    const deleteMutation = useDeleteCourseDraft({
        mutation: {
            onSuccess: ()=>{
                toast.success('강의 초안이 성공적으로 삭제되었습니다.');
                queryClient.invalidateQueries({
                    queryKey: [
                        'courseDrafts',
                        'my_drafts'
                    ]
                });
                onDelete(draft.id);
            },
            onError: (error)=>{
                console.error('Failed to delete course draft:', error);
                toast.error('강의 초안 삭제에 실패했습니다. 다시 시도해주세요.');
            },
            onSettled: ()=>{
                setIsDeleting(false);
            }
        }
    });
    const handleEditClick = (e)=>{
        e.stopPropagation(); // 카드 전체 클릭 방지
        onEdit(draft.id);
    };
    const handleDeleteClick = async (e)=>{
        e.stopPropagation();
        if (window.confirm(`'${draft.title || '이 초안'}'을(를) 정말 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.`)) {
            setIsDeleting(true);
            deleteMutation.mutate({
                id: draft.id
            });
        }
    };
    // updatedAt 포맷팅
    const formattedUpdatedAt = draft.updatedAt ? `${formatDistanceToNow(new Date(draft.updatedAt), {
        addSuffix: true,
        locale: ko
    })} 수정됨` : '날짜 정보 없음';
    return(// 최상위 요소인 CardContainer에 ref 연결
    /*#__PURE__*/ _jsxs(CardContainer, {
        ref: ref,
        children: [
            /*#__PURE__*/ _jsxs(ContentContainer, {
                children: [
                    /*#__PURE__*/ _jsx(CardHeader, {
                        children: /*#__PURE__*/ _jsxs(TitleWrapper, {
                            children: [
                                /*#__PURE__*/ _jsx(Title, {
                                    title: draft.title || '',
                                    children: draft.title || '제목 없음'
                                }),
                                /*#__PURE__*/ _jsx(PublishBadge, {
                                    isPublished: draft.isPublished,
                                    children: draft.isPublished ? '발행됨' : '초안'
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx(UpdatedAt, {
                        children: formattedUpdatedAt
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(ActionButtons, {
                children: [
                    /*#__PURE__*/ _jsx(Button, {
                        variant: "outline",
                        size: "sm",
                        onClick: handleEditClick,
                        icon: /*#__PURE__*/ _jsx(Edit, {
                            size: 14
                        }),
                        children: "편집"
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        variant: "outline",
                        size: "sm",
                        onClick: handleDeleteClick,
                        icon: /*#__PURE__*/ _jsx(Trash2, {
                            size: 14
                        }),
                        style: {
                            marginLeft: 'auto'
                        },
                        isLoading: isDeleting,
                        disabled: isDeleting,
                        children: isDeleting ? '삭제 중...' : '삭제'
                    })
                ]
            })
        ]
    }));
});
// forwardRef 사용 시 displayName 설정 권장 (디버깅 용이)
CourseDraftCard.displayName = 'CourseDraftCard';
export default CourseDraftCard;
